import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type MandantKurz } from '../lib/api'
import { Fenster } from '../teile/Fenster'
import { Filterfeld } from '../teile/Filterfeld'

const spalten = { gridTemplateColumns: '90px minmax(0, 1fr) 150px 110px' }
const kontenrahmen = ['SKR03', 'SKR04'] as const

export function MandantenSeite() {
  const { werkzeug } = useParams()
  const [suche, setzeSuche] = useState('')
  const [fensterOffen, setzeFensterOffen] = useState(false)

  const { data, isPending, error } = useQuery({
    queryKey: ['mandanten', suche],
    queryFn: () =>
      api.holen<{ mandanten: MandantKurz[] }>(
        `/api/mandanten${suche ? `?suche=${encodeURIComponent(suche)}` : ''}`,
      ),
  })

  return (
    <>
      {/* Die Leiste rahmt die Tabelle: suchen an der linken Kante,
          anlegen an der rechten. */}
      <div className="listenleiste">
        <Filterfeld
          className="suchfeld"
          wert={suche}
          setzeWert={setzeSuche}
          beschriftung="Mandanten filtern"
        />
        <button type="button" className="knopf" onClick={() => setzeFensterOffen(true)}>
          Mandant anlegen
        </button>
      </div>

      <div className="liste">
        <div className="listenkopf" style={spalten}>
          <div>Nummer</div>
          <div>Name</div>
          <div>Rechtsform</div>
          <div>Kontenrahmen</div>
        </div>

        {isPending && <div className="laedt">Wird geladen …</div>}

        {error && (
          <div className="leer">
            <strong>Die Mandanten konnten nicht geladen werden.</strong>
            {error instanceof Error ? error.message : ''}
          </div>
        )}

        {data && data.mandanten.length === 0 && (
          <div className="leer">
            {suche ? (
              <strong>Kein Mandant passt zu dieser Suche.</strong>
            ) : (
              <>
                <strong>Noch keine Mandanten angelegt.</strong>
                <p>Später kommen sie aus den DATEV Stammdaten. Bis dahin von Hand anlegen.</p>
              </>
            )}
          </div>
        )}

        {data?.mandanten.map((mandant) => (
          <Link key={mandant.id} to={`/${werkzeug}/mandanten/${mandant.id}`} className="zeile" style={spalten}>
            <div className="leiser zahl">{mandant.mandantennummer}</div>
            <div className="sache">{mandant.name}</div>
            <div className="leise">{mandant.rechtsform ?? '—'}</div>
            <div className="leise zahl">{mandant.kontenrahmen ?? '—'}</div>
          </Link>
        ))}
      </div>

      <MandantAnlegen offen={fensterOffen} aufSchliessen={() => setzeFensterOffen(false)} />
    </>
  )
}

function MandantAnlegen({ offen, aufSchliessen }: { offen: boolean; aufSchliessen: () => void }) {
  const abfragen = useQueryClient()
  const [name, setzeName] = useState('')
  const [beraternummer, setzeBeraternummer] = useState('')
  const [mandantennummer, setzeMandantennummer] = useState('')
  const [rechtsform, setzeRechtsform] = useState('')
  const [rahmen, setzeRahmen] = useState<string | null>(null)
  const [fehler, setzeFehler] = useState<string | null>(null)

  const anlegen = useMutation({
    mutationFn: () =>
      api.senden('/api/mandanten', {
        name: name.trim(),
        beraternummer: Number(beraternummer),
        mandantennummer: Number(mandantennummer),
        rechtsform: rechtsform.trim() || null,
        kontenrahmen: rahmen,
      }),
    onSuccess: async () => {
      await abfragen.invalidateQueries({ queryKey: ['mandanten'] })
      zuruecksetzen()
      aufSchliessen()
    },
    onError: (problem) => {
      setzeFehler(problem instanceof ApiFehler ? problem.message : 'Der Mandant konnte nicht angelegt werden.')
    },
  })

  function zuruecksetzen() {
    setzeName('')
    setzeBeraternummer('')
    setzeMandantennummer('')
    setzeRechtsform('')
    setzeRahmen(null)
    setzeFehler(null)
  }

  function absenden(ereignis: FormEvent) {
    ereignis.preventDefault()
    setzeFehler(null)
    anlegen.mutate()
  }

  return (
    <Fenster
      titel="Mandant anlegen"
      offen={offen}
      aufSchliessen={() => {
        zuruecksetzen()
        aufSchliessen()
      }}
    >
      <form className="formular" onSubmit={absenden}>
        {fehler && <div className="fehler">{fehler}</div>}

        <div>
          <label htmlFor="m-name">Name</label>
          <input id="m-name" type="text" required autoFocus value={name} onChange={(e) => setzeName(e.target.value)} />
        </div>

        <div className="feldreihe">
          <div>
            <label htmlFor="m-berater">Beraternummer</label>
            <input
              id="m-berater"
              type="number"
              required
              value={beraternummer}
              onChange={(e) => setzeBeraternummer(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="m-mandant">Mandantennummer</label>
            <input
              id="m-mandant"
              type="number"
              required
              value={mandantennummer}
              onChange={(e) => setzeMandantennummer(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label htmlFor="m-rechtsform">Rechtsform</label>
          <input
            id="m-rechtsform"
            type="text"
            placeholder="GmbH, e.K., GbR …"
            value={rechtsform}
            onChange={(e) => setzeRechtsform(e.target.value)}
          />
        </div>

        <div>
          <label>Kontenrahmen</label>
          <div className="wahl">
            {kontenrahmen.map((wert) => (
              <button
                key={wert}
                type="button"
                aria-pressed={rahmen === wert}
                onClick={() => setzeRahmen(rahmen === wert ? null : wert)}
              >
                {wert}
              </button>
            ))}
          </div>
        </div>

        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen}>
            Abbrechen
          </button>
          <button type="submit" className="knopf gefuellt" disabled={anlegen.isPending}>
            {anlegen.isPending ? 'Wird angelegt …' : 'Anlegen'}
          </button>
        </div>
      </form>
    </Fenster>
  )
}
